import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, DoorOpen, Clock, Users, ScanLine, Car, Brain, ChevronRight, Play, ArrowRight, Download, FileText, Video } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  {
    id: "access-control",
    title: "Access Control",
    icon: Shield,
    color: "from-blue-500 to-blue-700",
    description: "Comprehensive access control solutions for every environment",
    subcategories: [
      { id: "atlas", title: "Atlas Series", tagline: "Entry-Level Access Control Panels", description: "The ZKTeco Atlas Bio Series is a line of state-of-the-art biometric access control panels designed for efficient and integrated security management. Featuring a built-in web application, these panels offer advanced access control functionalities without requiring additional software. They support up to 5,000 users and 10,000 transactions per panel, with flexible reader compatibility and secure TCP/IP communication with SSL/TLS." },
      { id: "biocv", title: "Bio CVSecurity", tagline: "Intermediate Biometric Access Control", description: "Bio CVSecurity provides a comprehensive intermediate-level access control solution, combining biometric authentication with intelligent door management. Scalable from single-door installations to enterprise-wide deployments." },
      { id: "armatura", title: "Armatura One", tagline: "All-in-One Web-Based Integrated Security Platform", description: "Armatura One is our enterprise-grade, web-based integrated security platform, designed to unify access control, visitor management, video, intrusion, building automation, and wider security operations into one scalable solution. Built for organisations of all sizes, the platform supports expansion from single sites to global deployments with multi-server architecture, high availability, and the ability to manage 10,000+ edge devices and over one million users. Developed with cybersecurity at its core, Armatura One combines IEC 62443-4-1:2018 certified secure development practices, Grade 4 (EN 60839) compliance, AES encryption, TLS secure communication, ISO-certified security standards, and EAL6+ secure data protection to deliver exceptional levels of resilience, reliability, and protection. With open API integration, 260+ supported protocols, and advanced credential support including biometrics, mobile credentials, RFID, and QR codes, Armatura One provides a future-ready platform for modern enterprise and critical infrastructure environments.",
        heroImage: "/armatura/hero_banner.png",
        features: [
          { title: "Grade 4 Security", detail: "EN-60839 international standard for critical infrastructure" },
          { title: "Multi-Server Architecture", detail: "Hybrid on-premises and cloud with disaster recovery" },
          { title: "Unlimited Scalability", detail: "1 to 100+ sites, 5,000+ simultaneous clients, 1M+ users" },
          { title: "Multiple Authentication", detail: "Face, palm, fingerprint, mobile credentials, QR codes, RFID" },
          { title: "ISO Certified", detail: "ISO27001, ISO27701, ISO27017 information security" },
          { title: "260+ Protocols", detail: "BACnet, OPC, Modbus, ONVIF, RTSP, RESTful API, SDK" },
          { title: "Building Automation", detail: "HVAC, lighting, power, and environmental system control" },
          { title: "19 Languages", detail: "Global deployment with multi-language support" },
        ],
        gallery: [
          { id: "controllers", dualImages: ["/armatura/controller_silver.png", "/armatura/controller_black.png"], caption: "Controllers", clickable: true,
            subModules: [
              { id: "primary-controller", title: "Primary Controller - AHSC Series", image: "/armatura/controller_silver.png", description: "The Armatura AHSC-1000 Primary Controller is the main access control panel featuring a built-in touchscreen display, dual Ethernet ports (PoE), USB, Micro SD, RS-485, and RS-232 connectivity. Supports up to 4 doors with advanced features including global anti-passback, interlock, and multi-person access control.", datasheetUrl: "/armatura/ahsc_datasheet.pdf" },
              { id: "secondary-controller", title: "Secondary Controller - AHDU Series", image: "/armatura/controller_black.png", description: "The Armatura AHDU Series Secondary Controller extends your system capacity with additional door support. Features the same robust connectivity options and integrates seamlessly with the Primary Controller for large-scale deployments across multiple sites.", datasheetUrl: "/armatura/ahdu_datasheet.pdf" },
            ]},
          { id: "popular-readers", url: "/armatura/readers.png", caption: "Popular Readers", clickable: true,
            subModules: [
              { id: "face-palm-readers", title: "Face & Palm Series", image: "/armatura/face_and_palm.png", description: "Our Face and Palm range delivers advanced multi-technology access control with touchless facial and palm biometric authentication, RFID card support, mobile credentials, and QR code access. Combining AI-powered biometric recognition with liveness detection and multi-factor authentication, the range provides a secure and seamless user experience. Integrated SIP V2.0 video intercom functionality enables HD video calling, two-way audio communication, and visitor management, while secure OSDP communication and encrypted data protection ensure high levels of security. Designed for demanding installations, the range features a robust IP66 weatherproof and IK07 impact-rated enclosure for reliable indoor and outdoor performance.", datasheets: [
                { label: "OmniAC30", url: "/armatura/omniac30_datasheet.pdf" },
                { label: "FT10CMQ", url: "/armatura/ft10cmq_datasheet.pdf" },
                { label: "OmniAC20", url: "/armatura/omniac20_datasheet.pdf" },
              ] },
              { id: "qr-code-readers", title: "EP20 Series", image: "/armatura/qr_code.png", description: "The EP20 Series is our multi-tech reader range, designed to provide secure and flexible access control through RFID cards, mobile credentials, PIN authentication, and QR code options. Supporting over 100 credential technologies including 125kHz, 13.56MHz, NFC, and Bluetooth mobile access via Armatura ID, the EP20 Series adapts to a wide range of user requirements. Featuring secure OSDP communication, AES encryption, advanced data protection, and multi-factor authentication options, the range delivers enterprise-grade security in a compact design. With IP68 weather protection and up to IK10 impact resistance, the EP20 Series is built for reliable performance in both indoor and outdoor environments.", datasheetUrl: "/armatura/ep20c_series_datasheet.pdf" },
              { id: "fingerprint-readers", title: "Fingerprint Readers", image: "/armatura/ep30cf.png", description: "The EP30CF supports multiple credential options including 125kHz and 13.56MHz RFID cards, Bluetooth and NFC mobile credentials via Armatura ID, and advanced fingerprint biometric authentication. Supporting 100+ card technologies, the reader delivers flexible and secure authentication, with an IP65-rated enclosure providing protection against dust and water ingress.", datasheetUrl: "/armatura/ep30cf_datasheet.pdf" },
              { id: "ep10-range", title: "EP10 Range", image: "/armatura/ep10c.png", description: "The EP10 Series is our compact multi-tech reader range, supporting RFID cards, Bluetooth/NFC mobile credentials, and 100+ credential technologies. Featuring OSDP secure communication, AES encryption, EAL6+ data protection, Grade 4 compliance, and a rugged IP68 / IK10-rated design, the EP10 Series delivers enterprise security in a compact all-weather reader.", datasheetUrl: "/armatura/ep10c_datasheet.pdf" },
            ]},
          { id: "video-resource", videoLink: true, caption: "Video Resource", thumbnail: "/armatura/armatura_logo.png" },
        ],
      },
    ],
  },
  {
    id: "entrance-control",
    title: "Pedestrian Entrance Control",
    icon: DoorOpen,
    color: "from-green-500 to-green-700",
    description: "Speedlanes, turnstiles and pedestrian entrance control solutions",
    subcategories: [
      { id: "speedlanes", title: "Speedlane Series", tagline: "Comet, Saturn & Premium Speedlanes", description: "ZKTeco's speedlane range offers elegant, high-throughput pedestrian entrance control for corporate offices, transport hubs, and public buildings. Available in multiple configurations from the Comet to the premium Saturn series." },
      { id: "turnstiles", title: "Turnstiles", tagline: "Full Height & Half Height Turnstiles", description: "Robust turnstile solutions for high-security perimeters, stadiums, and industrial facilities. Available in full-height and half-height configurations with biometric and card reader integration." },
      { id: "gates", title: "Swing Gates", tagline: "ADA Compliant Swing Gate Solutions", description: "Swing gate solutions designed for accessibility compliance, providing controlled access for wheelchairs, pushchairs, and bulk goods whilst maintaining security." },
      { id: "fht-perimeter", title: "Full Height Turnstiles", tagline: "Full Height Perimeter Turnstiles", description: "Robust full height turnstiles for perimeter access control at industrial sites, stadiums, prisons, and high-security zones with anti-tailgating features." },
    ],
  },
  {
    id: "time-attendance",
    title: "Time & Attendance",
    icon: Clock,
    color: "from-purple-500 to-purple-700",
    description: "Workforce management and time tracking solutions",
    subcategories: [
      { id: "biometric-terminals", title: "Biometric Terminals", tagline: "Fingerprint, Face & Palm Recognition", description: "Advanced biometric terminals for accurate time and attendance tracking. Featuring fingerprint, facial recognition, and palm verification technologies for reliable workforce management." },
      { id: "software", title: "Software Solutions", tagline: "Cloud & On-Premise T&A Software", description: "Powerful time and attendance software platforms available as cloud-hosted or on-premise solutions, providing real-time reporting, shift management, and payroll integration." },
    ],
  },
  {
    id: "visitor-management",
    title: "Visitor Management",
    icon: Users,
    color: "from-teal-500 to-teal-700",
    description: "Professional visitor registration and management",
    subcategories: [
      { id: "vm-terminals", title: "Visitor Terminals", tagline: "Self-Service Visitor Kiosks", description: "Streamline your visitor experience with self-service registration kiosks featuring ID scanning, photo capture, badge printing, and pre-registration capabilities." },
      { id: "vm-software", title: "VM Software", tagline: "Visitor Management Platform", description: "Comprehensive visitor management software for pre-registration, check-in/out, host notification, compliance tracking, and detailed visitor analytics." },
    ],
  },
  {
    id: "security-inspection",
    title: "Security Inspection",
    icon: ScanLine,
    color: "from-amber-500 to-amber-700",
    description: "X-ray, metal detection and screening solutions",
    subcategories: [
      { id: "walkthrough", title: "Walk-Through Detectors", tagline: "Metal Detection Portals", description: "High-sensitivity walk-through metal detectors for airports, government buildings, events, and corporate environments with multi-zone detection and high throughput." },
      { id: "xray", title: "X-Ray Inspection", tagline: "Baggage & Parcel Screening", description: "Advanced X-ray inspection systems for baggage, parcels, and cargo screening with dual-energy imaging, threat detection algorithms, and operator-assist features." },
    ],
  },
  {
    id: "parking-management",
    title: "Perimeter Protection",
    icon: Car,
    color: "from-orange-500 to-orange-700",
    description: "Parking barriers, ANPR and perimeter security solutions",
    subcategories: [
      { id: "road-blocker", title: "Road Blocker", tagline: "High Security Road Blockers", description: "Heavy-duty road blockers providing the highest level of vehicle perimeter security for critical infrastructure, government buildings, and high-security facilities." },
      { id: "rising-bollards", title: "Rising Bollards", tagline: "Automatic Rising Bollard Systems", description: "Automatic and semi-automatic rising bollards for controlled vehicle access, pedestrian zone protection, and perimeter security with rapid deployment capabilities." },
      { id: "barriers", title: "Parking Barriers", tagline: "Automatic Barrier Systems", description: "Reliable automatic parking barrier systems for car parks, commercial premises, and residential sites with fast operation, vehicle detection, and integration options." },
      { id: "anpr", title: "ANPR Camera", tagline: "Automatic Number Plate Recognition", description: "Intelligent ANPR camera systems for frictionless vehicle access control, parking management, and security monitoring with real-time plate recognition." },
      { id: "tyre-killer", title: "Tyre Killer", tagline: "Anti-Vehicle Tyre Killer Systems", description: "Surface-mounted and flush tyre killer systems designed to prevent unauthorised vehicle access at high-security checkpoints, borders, and restricted areas." },
      { id: "uvis", title: "Under Vehicle Inspection System", tagline: "UVIS Scanning Technology", description: "Automated under vehicle inspection systems using high-resolution imaging to detect threats, contraband, and modifications beneath vehicles at security checkpoints." },
    ],
  },
  {
    id: "ai-analytics",
    title: "AI & Analytics Solutions",
    icon: Brain,
    color: "from-indigo-500 to-indigo-700",
    description: "Intelligent analytics and AI-powered security",
    subcategories: [
      { id: "people-counting", title: "People Counting", tagline: "Footfall Analytics & Reporting", description: "AI-powered people counting solutions providing accurate footfall data, occupancy monitoring, and detailed analytics for retail, corporate, and public spaces." },
      { id: "facial-analytics", title: "Facial Analytics", tagline: "AI-Powered Facial Recognition", description: "Advanced facial recognition and analytics for access control, VIP detection, watchlist alerting, and demographic analysis across multiple environments." },
      { id: "behaviour", title: "Behaviour Detection", tagline: "Smart Behaviour Analysis", description: "Intelligent video analytics detecting unusual behaviour patterns including loitering, crowd formation, perimeter breach, and abandoned objects." },
    ],
  },
];

function CategoryCard({ category, onClick }) {
  const Icon = category.icon;
  return (
    <button
      onClick={onClick}
      className="group relative bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all duration-300 text-left"
      data-testid={`pres-category-${category.id}`}
    >
      <div className={`h-2 w-full bg-gradient-to-r ${category.color}`} />
      <div className="p-5">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <h3 className="text-base font-bold text-slate-900 mb-1">{category.title}</h3>
        <p className="text-xs text-slate-500 leading-relaxed">{category.description}</p>
        <div className="flex items-center gap-1 mt-3 text-xs font-semibold text-slate-400 group-hover:text-slate-700 transition-colors">
          Explore <ChevronRight className="h-3 w-3" />
        </div>
      </div>
    </button>
  );
}

function ProductLandingPage({ sub, category, onBack, assignments, datasheets }) {
  const Icon = category.icon;
  const assignedVideoId = (assignments || {})[sub.id];
  const datasheet = (datasheets || {})[sub.id];
  const [showVideo, setShowVideo] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  const videoStreamUrl = assignedVideoId ? `${API}/resource/${assignedVideoId}/stream` : null;
  const videoDownloadUrl = assignedVideoId ? `${API}/resource/${assignedVideoId}/download` : null;
  const datasheetUrl = datasheet ? `${API}/presentation/datasheet/${sub.id}/download` : null;

  const features = sub.features || [];
  const heroImage = sub.heroImage || null;
  const galleryImages = sub.gallery || [];

  const activeSectionData = galleryImages.find(g => g.id === activeSection);

  if (activeSectionData && activeSectionData.subModules) {
    return (
      <div>
        <button onClick={() => setActiveSection(null)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to {sub.title}
        </button>
        <div className="flex items-center gap-3 mb-6">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${category.color} flex items-center justify-center`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">{activeSectionData.caption}</h2>
            <p className="text-sm text-slate-500">{sub.title}</p>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {activeSectionData.subModules.map((mod) => (
            <div key={mod.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`sub-module-${mod.id}`}>
              <div className="h-52 bg-slate-50 flex items-center justify-center p-4">
                {mod.image ? (
                  <img src={mod.image} alt={mod.title} className="max-h-full max-w-full object-contain" />
                ) : (
                  <div className="text-center">
                    <Icon className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-xs text-slate-400">Image coming soon</p>
                  </div>
                )}
              </div>
              <div className="p-5">
                <h3 className="text-base font-bold text-slate-900 mb-2">{mod.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{mod.description}</p>
                {mod.datasheetUrl && (
                  <a href={mod.datasheetUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors" data-testid={`datasheet-${mod.id}`}>
                    <Download className="h-4 w-4" /> Download Datasheet
                  </a>
                )}
                {mod.datasheets && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {mod.datasheets.map((ds) => (
                      <a key={ds.label} href={ds.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-colors" data-testid={`datasheet-${ds.label}`}>
                        <Download className="h-3.5 w-3.5" /> {ds.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to {category.title}
      </button>

      {/* Video Modal */}
      {showVideo && videoStreamUrl && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowVideo(false)}>
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end mb-2">
              <button onClick={() => setShowVideo(false)} className="text-white/70 hover:text-white text-sm">Close</button>
            </div>
            <div className="aspect-video bg-black rounded-xl overflow-hidden">
              <video controls autoPlay playsInline className="w-full h-full">
                <source src={videoStreamUrl} type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className={`relative rounded-2xl overflow-hidden ${heroImage ? 'h-72 sm:h-80' : ''} bg-gradient-to-br ${category.color} mb-6`}>
        {heroImage && (
          <img src={heroImage} alt={sub.title} className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-40" />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
        <div className="relative h-full px-8 py-10 sm:py-14 flex flex-col justify-end">
          <div className="flex items-center gap-2 mb-2">
            <Icon className="h-4 w-4 text-white/80" />
            <span className="text-white/80 text-xs font-semibold uppercase tracking-wider">{category.title}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-1">{sub.title}</h1>
          <p className="text-white/80 text-sm sm:text-base max-w-xl">{sub.tagline}</p>
        </div>
      </div>

      {/* Action Buttons Row */}
      <div className="flex flex-wrap gap-3 mb-6">
        {videoStreamUrl && (
          <button onClick={() => setShowVideo(true)} className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors" data-testid={`watch-video-${sub.id}`}>
            <Play className="h-4 w-4 fill-white" /> Watch Video
          </button>
        )}
        {videoDownloadUrl && (
          <a href={videoDownloadUrl} download className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors">
            <Video className="h-4 w-4" /> Download Video
          </a>
        )}
        {datasheetUrl && (
          <a href={datasheetUrl} download className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors" data-testid={`download-datasheet-${sub.id}`}>
            <FileText className="h-4 w-4" /> Download Datasheet
          </a>
        )}
      </div>

      {/* Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Overview */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-3">Overview</h2>
            <p className="text-sm text-slate-600 leading-relaxed">{sub.description}</p>
          </div>

          {/* Key Features */}
          {features.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Key Features</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {features.map((f, i) => (
                  <div key={f.title} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${category.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <span className="text-white text-xs font-bold">{i + 1}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{f.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{f.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Gallery Sidebar */}
        <div className="space-y-4">
          {galleryImages.map((img, i) => {
            const isClickable = img.clickable && img.subModules;
            const Wrapper = isClickable ? "button" : "div";
            const wrapperProps = isClickable ? { onClick: () => setActiveSection(img.id) } : {};
            return (
              <Wrapper key={img.caption || `gallery-${i}`} {...wrapperProps} className={`rounded-xl overflow-hidden border border-slate-200 shadow-sm w-full text-left ${isClickable ? 'hover:shadow-md hover:border-slate-300 transition-all cursor-pointer group' : ''}`} data-testid={`gallery-${img.id || i}`}>
                {img.dualImages ? (
                  <div className="flex gap-3 p-4 bg-slate-50 justify-center items-center">
                    {img.dualImages.map((src, j) => (
                      <img key={src} src={src} alt={img.caption} className="h-32 w-auto object-contain" />
                    ))}
                  </div>
                ) : img.videoLink ? (
                  videoStreamUrl ? (
                    <button onClick={() => setShowVideo(true)} className="w-full h-48 relative overflow-hidden flex items-center justify-center cursor-pointer group">
                      {img.thumbnail ? (
                        <img src={img.thumbnail} alt="Video Resource" className="w-full h-full object-contain bg-slate-900" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-900" />
                      )}
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-all flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                          <Play className="h-6 w-6 text-white fill-white ml-0.5" />
                        </div>
                      </div>
                    </button>
                  ) : (
                    <div className="w-full h-48 relative overflow-hidden">
                      {img.thumbnail ? (
                        <img src={img.thumbnail} alt="Video Resource" className="w-full h-full object-contain bg-slate-900" />
                      ) : (
                        <div className="w-full h-full bg-slate-50 flex items-center justify-center">
                          <p className="text-sm text-slate-400">No video assigned</p>
                        </div>
                      )}
                    </div>
                  )
                ) : img.url ? (
                  <div className="h-48 bg-slate-50 flex items-center justify-center p-3">
                    <img src={img.url} alt={img.caption || sub.title} className="max-h-full max-w-full object-contain" />
                  </div>
                ) : img.placeholder ? (
                  <div className="h-48 bg-slate-50 flex items-center justify-center">
                    <p className="text-sm text-slate-400">Coming soon</p>
                  </div>
                ) : null}
                {img.caption && (
                  <div className="flex items-center justify-between px-3 py-2 bg-white">
                    <p className="text-xs text-slate-500">{img.caption}</p>
                    {isClickable && <ChevronRight className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-700 transition-colors" />}
                  </div>
                )}
              </Wrapper>
            );
          })}
          {galleryImages.length === 0 && !videoStreamUrl && !datasheetUrl && (
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 text-center">
              <p className="text-sm text-slate-400">More resources coming soon</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SubcategoryView({ category, onBack, assignments, datasheets }) {
  const [selectedSub, setSelectedSub] = useState(null);
  const Icon = category.icon;

  if (selectedSub) {
    return (
      <ProductLandingPage
        sub={selectedSub}
        category={category}
        onBack={() => setSelectedSub(null)}
        assignments={assignments}
        datasheets={datasheets}
      />
    );
  }

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to Overview
      </button>
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">{category.title}</h2>
          <p className="text-sm text-slate-500">{category.description}</p>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {category.subcategories.map((sub) => {
          const hasVideo = !!(assignments || {})[sub.id];
          const hasDatasheet = !!(datasheets || {})[sub.id];
          return (
            <button
              key={sub.id}
              onClick={() => setSelectedSub(sub)}
              className="group bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md hover:border-slate-300 transition-all text-left"
              data-testid={`pres-sub-${sub.id}`}
            >
              <div className={`w-full h-32 rounded-lg bg-gradient-to-br ${category.color} opacity-10 mb-3 flex items-center justify-center`}>
                <Icon className="h-10 w-10 text-slate-400" />
              </div>
              <h3 className="text-sm font-bold text-slate-900 mb-0.5">{sub.title}</h3>
              <p className="text-xs text-slate-500 mb-2">{sub.tagline}</p>
              <div className="flex items-center gap-2">
                {hasVideo && <span className="text-[10px] font-semibold text-red-500 bg-red-50 px-1.5 py-0.5 rounded">VIDEO</span>}
                {hasDatasheet && <span className="text-[10px] font-semibold text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">DATASHEET</span>}
              </div>
              <div className="flex items-center gap-1 mt-2 text-xs font-semibold text-slate-400 group-hover:text-slate-700 transition-colors">
                View Details <ArrowRight className="h-3 w-3" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ZKTecoPresentation({ onBack }) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [assignments, setAssignments] = useState({});
  const [datasheets, setDatasheets] = useState({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const [assignRes, dsRes] = await Promise.all([
          axios.get(`${API}/resources/video-assignments`, { headers }),
          axios.get(`${API}/presentation/datasheets`, { headers }),
        ]);
        setAssignments(assignRes.data || {});
        setDatasheets(dsRes.data || {});
      } catch (err) {
        console.error("Failed to fetch presentation data:", err);
      }
    };
    fetchData();
  }, []);

  const category = CATEGORIES.find((c) => c.id === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-600 hover:text-slate-900" data-testid="presentation-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>

        {!selectedCategory ? (
          <>
            <div className="text-center mb-10">
              <img src="/zkteco-logo.png" alt="ZKTeco" className="h-12 w-auto mx-auto mb-4" />
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-testid="presentation-title">
                ZKTeco Product Portfolio
              </h1>
              <p className="text-sm text-slate-500 max-w-xl mx-auto">
                Explore our comprehensive range of security and workforce management solutions. Click on a category to learn more.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {CATEGORIES.map((cat) => (
                <CategoryCard
                  key={cat.id}
                  category={cat}
                  onClick={() => setSelectedCategory(cat.id)}
                />
              ))}
            </div>
          </>
        ) : (
          <SubcategoryView
            category={category}
            onBack={() => setSelectedCategory(null)}
            assignments={assignments}
            datasheets={datasheets}
          />
        )}
      </div>
    </div>
  );
}
